/* Free gifts in Sidecart V2 — reminding, and getting them there quickly
   ---------------------------------------------------------------------------
   Two jobs, both about the moment the cart drawer opens.

   REMINDING. Globo campaigns where the shopper picks which gift they want are
   claimed on the product page, so anyone who adds a qualifying product from
   somewhere else (a landing page build sheet, a collection, a quick-add)
   reaches the cart with a gift they are entitled to and have not taken. This
   puts that back in front of them at the last moment, in the cart, with the
   choice inline so they never have to go back to the product page. Campaigns
   that add their own gift (isAutoAdd) are never reminded about — the gift is
   coming either way.

   GETTING THEM THERE. The app takes about two seconds after the add-to-cart to
   put a gift in the cart, and the drawer opens inside that gap showing a cart
   with the gift missing. So a gift the shopper has already chosen is claimed
   here as soon as the trigger product is added, instead of waited for.

   Only a chosen gift, and only for campaigns the app does not add for itself.
   The app works out what to add from a snapshot of the cart taken before ours
   would land, so racing it on an automatic gift gets a second copy added — and
   only one of the two comes back free. That is a customer charged for a free
   gift to save two seconds, so those are left entirely alone.

   Everything shown is read from the app's own live campaign data
   (window.gfg.settings.freeGifts) — which products trigger a campaign, which
   gifts it offers, what they are called. Nothing about the offer is duplicated
   in the theme, so a campaign edited in the Globo admin is reflected here with
   no theme change, and the reminder cannot promise a gift the campaign has
   stopped giving.

   The claim posts the same line-item properties the app posts (see
   gfg.gfgFreeGift.f.gfgAddFreeGiftToCart): _free_product, _rule_id and
   _kite_promo_name are what the app's Shopify discount function matches on to
   take the price to zero. Because being wrong about that would CHARGE someone
   for a gift, the add is verified against the cart afterwards and rolled back
   if the line did not come back free — see claim().

   Eligibility FAILS CLOSED, and is asked of the app rather than worked out
   again here (see customerEligible). A campaign whose conditions cannot be
   answered for, or that is URL-gated or outside its schedule, is treated as not
   applicable: the app still carries the offer on the product page, and saying
   nothing costs less than promising a gift the shopper does not qualify for. */

(function () {
  if (window.sidecartFreeGiftLoaded) return;
  window.sidecartFreeGiftLoaded = true;

  var BLOCK_CLASS = "sc2-fg";
  var MARKER = "data-sc2-fg";
  // Placed above the line items: a reminder that scrolls below the fold is not
  // a reminder.
  var HOST = ".sc2-body";

  var lastCart = null;
  var currentOffer = null;
  // Set when a claim is undone after the cart was already repainted.
  var lateFailure = "";
  var queued = false;

  // ── Campaign reading ──────────────────────────────────────────────────────

  function campaigns() {
    var fg = window.gfg && gfg.settings && gfg.settings.freeGifts;
    return Array.isArray(fg) ? fg : [];
  }

  function scheduleActive(campaign) {
    var s = campaign.campaignScheduleData;
    if (!s || s.campaignSchedule === "CONTINUOUS") return true;
    var start = s.startDateTime_UTC || s.startDateTime;
    var end = s.endDateTime_UTC || s.endDateTime;
    if (!start || !end) return false;
    var now = Date.now();
    return now >= Date.parse(start) && now <= Date.parse(end);
  }

  /* Campaigns this reminder can speak for. Auto-add campaigns are excluded on
     purpose — the app has already put the gift in the cart, so there is nothing
     to remind anyone about. */
  /* Whether this shopper qualifies — markets, customer tags, being logged in.
     Asked of the app rather than worked out again here: three of these campaigns
     are restricted by market, and a second implementation of that would be one
     more thing to keep in step with the admin. Fails closed, so a campaign whose
     conditions cannot be answered for is treated as not applicable. */
  function customerEligible(index) {
    var campaign = campaigns()[index];
    var conditions = campaign && campaign.conditionsData && campaign.conditionsData.conditionsList;
    if (!conditions || !conditions.length) return true;

    var ask =
      window.gfg && gfg.gfgFreeGift && gfg.gfgFreeGift.f &&
      gfg.gfgFreeGift.f.gfgFreeGiftCheckCustomerConditions;
    if (typeof ask !== "function") return false;
    try {
      return ask(index) === true;
    } catch (e) {
      return false;
    }
  }

  function claimableCampaigns() {
    return campaigns().reduce(function (out, campaign, index) {
      if (!campaign || campaign.isEnabled === false) return out;
      if (campaign.isAutoAdd) return out;
      if (campaign.isCampaignUrlEnabled) return out;
      if (!scheduleActive(campaign)) return out;
      if (!customerEligible(index)) return out;
      out.push({ campaign: campaign, index: index });
      return out;
    }, []);
  }

  // Does this cart line satisfy one of the rule's trigger products?
  function isTrigger(item, entry) {
    if (String(item.product_id) !== String(entry.productId)) return false;
    /* A product listed with specific variants only triggers on those. Where the
       app recorded the product as single-variant the list is the whole product,
       so the id match above is the whole test. */
    var variants = entry.variants || [];
    if (entry.hasOnlyDefaultVariant || !variants.length) return true;
    return variants.some(function (v) {
      return String(v.variantId) === String(item.variant_id);
    });
  }

  function giftVariantIds(rule) {
    var ids = [];
    (rule.freeGiftProduct || []).forEach(function (gift) {
      (gift.variants || []).forEach(function (v) { ids.push(String(v.variantId)); });
    });
    return ids;
  }

  /* The gift is claimed when a free-gift line in the cart is one of this
     campaign's own gift variants. Matching on the variant rather than on the
     promo name keeps two campaigns offering the same product from cancelling
     each other out. */
  function alreadyClaimed(cart, rule) {
    var ids = giftVariantIds(rule);
    return cart.items.some(function (item) {
      var props = item.properties || {};
      return props._free_product === "true" && ids.indexOf(String(item.variant_id)) !== -1;
    });
  }

  /* The app keeps a live, variant-keyed snapshot of the gift products it fetched
     from Shopify — real stock and a real price in the shopper's own currency.
     The campaign settings cannot be trusted for either: they are a snapshot
     taken when the campaign was saved, in shop currency, and their `handle` goes
     stale the moment a merchant renames a product (as one of the floor mats
     already has). Ids are stable, so the variant id is the only thing read
     across from the campaign and the only thing the claim posts. */
  function liveVariant(variantId) {
    var map =
      window.gfg &&
      gfg.gfgFreeGift &&
      gfg.gfgFreeGift.state &&
      gfg.gfgFreeGift.state.freeGiftsShopifyDataByVariantIds;
    return (map && map[variantId]) || null;
  }

  function gifts(rule) {
    return (rule.freeGiftProduct || []).reduce(function (out, gift) {
      var variant = (gift.variants || [])[0];
      if (!variant) return out;
      var id = String(variant.variantId);
      var live = liveVariant(id);
      // Sold out is the app's own reason to refuse a claim, so do not offer it.
      if (live && live.available === false) return out;
      out.push({
        variantId: id,
        // The chooser holds a pick by product id, which is what the app's own
        // widget rows are keyed by.
        productId: String(gift.productId || ""),
        title: gift.title || (live && live.name) || "",
        // Only worth showing when it is not just "Default Title".
        variantTitle: /default title/i.test(variant.variantTitle || "") ? "" : (variant.variantTitle || ""),
        image: variant.variantImage || ((gift.images || [])[0] || {}).originalSrc || (live && live.image) || "",
        price: live ? live.price : null,
      });
      return out;
    }, []);
  }

  /* The first unclaimed offer this cart has earned. One at a time: two gift
     panels in a cart drawer read as clutter rather than as good news, and a
     second campaign surfaces as soon as the first is claimed. */
  function findOffer(cart) {
    if (!cart || !cart.items || !cart.items.length) return null;

    var found = null;
    claimableCampaigns().some(function (entry) {
      var rules = entry.campaign.rulesList || [];
      return rules.some(function (rule, ruleIndex) {
        if (rule.ruleType !== "BUY_PRODUCT_X") return false;

        var qty = 0;
        var triggerTitle = "";
        cart.items.forEach(function (item) {
          var props = item.properties || {};
          if (props._free_product === "true") return;
          var hit = (rule.productList || []).some(function (p) { return isTrigger(item, p); });
          if (!hit) return;
          qty += item.quantity;
          if (!triggerTitle) triggerTitle = item.product_title || item.title || "";
        });

        var min = parseInt(rule.minProducts, 10);
        if (!qty || qty < (isNaN(min) ? 1 : min)) return false;
        if (alreadyClaimed(cart, rule)) return false;

        var options = gifts(rule);
        if (!options.length) return false;

        found = {
          /* The key is what makes a repaint a no-op, so it has to change when
             the panel would render differently — including once the app's live
             variant data arrives and the gifts can show what they are worth. */
          key: entry.index + ":" + ruleIndex + ":" + options.map(function (g) {
            return g.variantId + "@" + (g.price == null ? "?" : g.price);
          }).join(","),
          campaignIndex: entry.index,
          // What the app itself stamps on the line, and what its discount
          // function matches on.
          promoName:
            (entry.campaign.discountData_shopifyResponse &&
              entry.campaign.discountData_shopifyResponse.title) ||
            entry.campaign.name,
          ruleId: rule.ruleType + "_" + ruleIndex,
          triggerTitle: triggerTitle,
          gifts: options,
        };
        return true;
      });
    });

    return found;
  }

  // ── Price ─────────────────────────────────────────────────────────────────

  function symbol() {
    var code = (window.Shopify && Shopify.currency && Shopify.currency.active) || "GBP";
    return { GBP: "£", EUR: "€", USD: "$" }[code] || "";
  }

  function worth(gift) {
    var sym = symbol();
    if (!gift.price || !sym) return "";
    return sym + (gift.price / 100).toFixed(2);
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function sidecart() {
    return document.querySelector("sidecart-v2");
  }

  function giftLabel(gift) {
    return gift.variantTitle ? gift.title + " — " + gift.variantTitle : gift.title;
  }

  function rowHTML(gift, single) {
    var value = worth(gift);
    return (
      '<li class="sc2-fg__row">' +
      (gift.image
        ? '<img class="sc2-fg__img" src="' + esc(gift.image) + '" alt="" width="38" height="38" loading="lazy">'
        : '<span class="sc2-fg__img sc2-fg__img--empty"></span>') +
      '<span class="sc2-fg__name">' +
      esc(giftLabel(gift)) +
      (value ? '<small class="sc2-fg__worth">Worth ' + esc(value) + "</small>" : "") +
      "</span>" +
      '<button type="button" class="sc2-fg__btn" data-sc2-fg-claim="' + esc(gift.variantId) + '">' +
      (single ? "Add it free" : "Choose") +
      "</button></li>"
    );
  }

  function blockHTML(offer) {
    var single = offer.gifts.length === 1;
    var lead = single
      ? "You have a free " + esc(offer.gifts[0].title) + " waiting"
      : "You have a free gift waiting";
    var sub = offer.triggerTitle
      ? "Your " + esc(offer.triggerTitle) + " comes with " + (single ? "it" : "one of these") + ", free. " +
        (single ? "Add it before you check out." : "Pick the one you want.")
      : (single ? "Add it before you check out." : "Pick the one you want.");

    return (
      '<div class="' + BLOCK_CLASS + '" ' + MARKER + '="' + esc(offer.key) + '">' +
      '<div class="sc2-fg__head">' +
      '<span class="sc2-fg__badge">Free gift</span>' +
      '<span class="sc2-fg__lead">' + lead + "</span>" +
      "</div>" +
      '<p class="sc2-fg__sub">' + sub + "</p>" +
      '<ul class="sc2-fg__list">' +
      offer.gifts.map(function (g) { return rowHTML(g, single); }).join("") +
      "</ul>" +
      '<p class="sc2-fg__error" hidden></p>' +
      "</div>"
    );
  }

  function removeBlock() {
    var existing = document.querySelector("." + BLOCK_CLASS);
    if (existing) existing.remove();
  }

  function paint(offer) {
    var cart = sidecart();
    if (!cart) return;
    var host = cart.querySelector(HOST);
    if (!host) return;

    var existing = host.querySelector("." + BLOCK_CLASS);
    // Idempotent: an unchanged offer must not rewrite the DOM, or the observer
    // that re-inserts after a section re-render would drive itself in a loop.
    if (existing && existing.getAttribute(MARKER) === offer.key) return;
    if (existing) existing.remove();

    host.insertAdjacentHTML("afterbegin", blockHTML(offer));
    /* A claim that was rolled back after the cart had already been repainted has
       no button left to report to, so it leaves its apology here for whichever
       panel comes back. */
    if (lateFailure) {
      setError(lateFailure);
      lateFailure = "";
    }
    trackClarity("sidecart_free_gift_reminder_shown");
  }

  function trackClarity(name) {
    if (typeof window.clarity !== "function") return;
    try { window.clarity("event", name); } catch (e) {}
  }

  // ── Claiming ──────────────────────────────────────────────────────────────

  function setError(message) {
    var el = document.querySelector("." + BLOCK_CLASS + " .sc2-fg__error");
    if (!el) return;
    el.textContent = message || "";
    el.hidden = !message;
  }

  /* The line is only a gift if the app's discount actually zeroed it. It is a
     cart-level Shopify function, so it can lag the add by a moment — hence one
     retry before giving up. Anything still not free is removed again rather
     than left in the cart at full price under a "free gift" heading. */
  function verifyFree(key, attempt, done) {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var line = cart.items.filter(function (i) { return i.key === key; })[0];
        if (line && line.final_line_price === 0) return done(true, cart);
        if (attempt < 1) return setTimeout(function () { verifyFree(key, attempt + 1, done); }, 700);
        done(false, cart);
      })
      .catch(function () { done(false, null); });
  }

  function rollback(key, then) {
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .catch(function () {})
      .then(then, then);
  }

  /* Paints the cart from sections Shopify rendered as part of a cart write,
     which is how sidecart-v2 renders its own updates — borrowing its section
     list and its after-render hook so the zone picker, the carousel and the
     Route widget all come back up the way they do everywhere else. */
  function renderSections(sc, state) {
    if (!sc || !state || !state.sections) return false;
    if (typeof sc.getSectionsToRender !== "function") return false;

    var painted = false;
    sc.getSectionsToRender().forEach(function (section) {
      var host = document.getElementById(section.id);
      var html = state.sections[section.section];
      if (!host || !html) return;
      var target = host.querySelector(section.selector) || host;
      target.innerHTML = sc.getSectionInnerHTML(html, section.selector);
      painted = true;
    });
    if (painted && typeof sc._afterRender === "function") sc._afterRender();
    return painted;
  }

  /* Adds the gift the way the app adds it, shows it as soon as it is in, and
     checks the price afterwards rather than making the shopper wait on it.

     Ordering the render before the check is safe because the check can only
     ever take the line away again — never leave something charged sitting in
     the cart. Waiting for it first cost a round trip in every case, to guard
     against one that has not happened yet.

     done(ok) is called once the line is in the cart and on screen; a later
     failure arrives as a re-render with the panel back and an apology on it. */
  function addGift(variantId, offer, done) {
    var sc = sidecart();
    var sections = sc && typeof sc.getSectionsToRender === "function" ? sc.getSectionsToRender() : [];

    var body = {
      items: [{
        id: Number(variantId),
        quantity: 1,
        properties: {
          _free_product: "true",
          _rule_id: offer.ruleId,
          _kite_promo_name: offer.promoName,
        },
      }],
    };
    // Shopify can render the cart in the same response as the write, which
    // saves fetching it back separately just to show what we already know.
    if (sections.length) {
      body.sections = sections.map(function (section) { return section.section; });
      body.sections_url = window.location.pathname;
    }

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (response) {
        var added = (response.items || [response])[0];
        if (!added || !added.key) return Promise.reject();

        if (!renderSections(sc, response)) {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          if (sc && typeof sc.refresh === "function") sc.refresh();
        }
        done(true);

        verifyFree(added.key, 0, function (free, cart) {
          if (free) {
            lastCart = cart;
            sync();
            return;
          }

          /* The discount did not land — the campaign may have changed under us.
             Undo the add rather than leave a charged line in a cart under a
             "free gift" heading. */
          rollback(added.key, function () {
            lateFailure = "We could not add that gift after all — nothing has been charged. Please try again.";
            reload();
          });
        });
      })
      .catch(function () { done(false); });
  }

  function claim(button) {
    var offer = currentOffer;
    if (!offer) return;

    var buttons = document.querySelectorAll("." + BLOCK_CLASS + " .sc2-fg__btn");
    Array.prototype.forEach.call(buttons, function (b) { b.disabled = true; });
    var label = button.textContent;
    button.textContent = "Adding…";
    setError("");

    addGift(button.getAttribute("data-sc2-fg-claim"), offer, function (ok) {
      if (ok) {
        trackClarity("sidecart_free_gift_claimed");
        // The re-render drops the block; sync() then finds nothing to offer.
        button.textContent = "Added ✓";
        return;
      }
      Array.prototype.forEach.call(buttons, function (b) { b.disabled = false; });
      button.textContent = label;
      /* No product link is offered with the apology: the campaign's stored
         handle goes stale when a merchant renames a gift, and a dead link is
         worse than no link. */
      setError("We could not add that gift just now — nothing has been charged. Please try again.");
    });
  }

  /* The shopper picked a gift on the product page and the cart now qualifies,
     so claim it here and now. Left to itself the app takes about five seconds
     over this — it will not enable its own claim button until it has noticed the
     cart change, and only then starts adding — which is a long time to sit in an
     open cart with the gift you chose missing from it.

     Taking the pick off the chooser first is what keeps this from double-adding:
     released, it will never forward its own claim. */
  function fastClaim(offer, gift) {
    if (window.freeGiftChoiceHeld) {
      if (typeof window.freeGiftChooserRelease !== "function") return;
      window.freeGiftChooserRelease();
    }
    // Read by choicePending(), so nothing paints while this is in the air.
    window.freeGiftClaimInFlight = Date.now();
    fastClaiming = true;
    addGift(gift.variantId, offer, function (ok) {
      fastClaiming = false;
      if (ok) trackClarity("free_gift_fast_claimed");
      // A failure leaves the cart as it was, so the panel takes over as usual.
      else window.freeGiftClaimInFlight = 0;
    });
  }

  // The gift this offer's shopper is already holding a pick for, if any.
  function heldGift(offer) {
    var held = window.freeGiftHeldChoice;
    if (!held) return null;
    return offer.gifts.filter(function (g) { return g.productId === String(held); })[0] || null;
  }

  function offerFor(entry, rule, ruleIndex, options) {
    return {
      key: entry.index + ":" + ruleIndex,
      campaignIndex: entry.index,
      promoName:
        (entry.campaign.discountData_shopifyResponse &&
          entry.campaign.discountData_shopifyResponse.title) ||
        entry.campaign.name,
      ruleId: rule.ruleType + "_" + ruleIndex,
      gifts: options,
    };
  }

  /* Walks the given campaigns for one the line just added earns, and claims its
     gift — choose() says which, and returning nothing from it passes. */
  function tryClaimFromAdd(entries, line, choose) {
    var claimed = false;

    entries.some(function (entry) {
      return (entry.campaign.rulesList || []).some(function (rule, ruleIndex) {
        if (rule.ruleType !== "BUY_PRODUCT_X") return false;

        var options = gifts(rule);
        var gift = choose(options, rule);
        if (!gift) return false;

        // Only the line just added is known here, so a rule wanting more than
        // it goes the long way round.
        var min = parseInt(rule.minProducts, 10);
        if (line.quantity < (isNaN(min) ? 1 : min)) return false;
        if (!(rule.productList || []).some(function (p) { return isTrigger(line, p); })) return false;
        // Never a second copy of a gift the cart already has. Without a cart to
        // check against, leave it to the slower path that reads one.
        if (!lastCart || alreadyClaimed(lastCart, rule)) return false;

        fastClaim(offerFor(entry, rule, ruleIndex, options), gift);
        claimed = true;
        return true;
      });
    });

    return claimed;
  }

  /* Claims off the add-to-cart itself, without reading the cart back first to
     confirm what the event has just told us. The read was a whole round trip in
     the middle of the one moment the shopper is watching, so it is spent only
     when this cannot answer on its own. Returns whether a claim went ahead. */
  function claimFromAdd(product) {
    if (!product || fastClaiming || claimInFlight()) return false;

    var line = {
      product_id: product.product_id,
      variant_id: product.variant_id || product.id,
      quantity: product.quantity || 1,
    };

    /* Only a gift the shopper chose. Campaigns that add their own (isAutoAdd)
       are deliberately left alone: the app decides what to add from a snapshot
       of the cart it took before this add would land, so it does not see the
       line and adds a second one — and only one of the two comes back free.
       Measured: a chair, a free set of armrest pads, and a second set at
       £24.95. Two seconds of waiting is worth a great deal less than that. */
    var held = window.freeGiftHeldChoice;
    if (!held) return false;

    return tryClaimFromAdd(claimableCampaigns(), line, function (options) {
      return options.filter(function (g) { return g.productId === String(held); })[0] || null;
    });
  }

  // ── Wiring ────────────────────────────────────────────────────────────────

  var priceWaits = 0;
  var recheck = null;

  function later(ms) {
    clearTimeout(recheck);
    recheck = setTimeout(reload, ms);
  }

  /* A gift chosen on the product page reaches the cart around five seconds after
     the product does, and the drawer opens inside that gap — so a cart with no
     gift in it is not yet evidence that the shopper has not chosen one.
     free-gift-chooser.js publishes both halves of that wait (see its comment on
     setPendingPick); until they are over, the panel has nothing to say. */
  var CLAIM_GRACE_MS = 12000;
  var fastClaiming = false;

  function claimInFlight() {
    var at = window.freeGiftClaimInFlight;
    return !!at && Date.now() - at < CLAIM_GRACE_MS;
  }

  function choicePending() {
    return !!window.freeGiftChoiceHeld || claimInFlight();
  }

  function sync() {
    if (!campaigns().length) return;
    currentOffer = findOffer(lastCart);
    if (!currentOffer) {
      removeBlock();
      return;
    }

    var held = heldGift(currentOffer);
    if (held && !fastClaiming && !claimInFlight()) {
      removeBlock();
      fastClaim(currentOffer, held);
      later(600);
      return;
    }

    if (choicePending()) {
      removeBlock();
      later(400);
      return;
    }

    paint(currentOffer);

    /* The app fills its variant map asynchronously, so an early paint can be
       missing the prices — and, more importantly, the stock check that decides
       whether a gift is offered at all. Come back for it. */
    var incomplete = currentOffer.gifts.some(function (g) { return g.price == null; });
    if (incomplete && priceWaits < 25) {
      priceWaits++;
      setTimeout(sync, 500);
    }
  }

  /* Re-reads the cart rather than reusing the last copy: the app re-renders the
     sidecart itself when it adds a gift, and that arrives here as a DOM change
     with no event, so a cached cart would still be the one from before. */
  function schedule() {
    if (queued) return;
    queued = true;
    setTimeout(function () {
      queued = false;
      reload();
    }, 250);
  }

  function reload() {
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastCart = cart;
        sync();
      })
      .catch(function () {});
  }

  function start() {
    var sc = sidecart();
    if (!sc) return;

    sc.addEventListener("click", function (event) {
      var button = event.target.closest("[data-sc2-fg-claim]");
      if (button) claim(button);
    });

    ["cart:refresh", "line-item:change:end", "cart-drawer:open"].forEach(function (name) {
      document.addEventListener(name, reload);
    });

    document.addEventListener("cart:item-added", function (event) {
      var product = event.detail && event.detail.product;
      // The cart still has to be re-read either way; claiming first just means
      // not making the shopper wait for that before their gift appears.
      if (!claimFromAdd(product)) reload();
    });

    /* The sidecart replaces its own markup on every quantity change and refresh,
       which takes the block with it — put it back whenever that happens. */
    new MutationObserver(schedule).observe(sc, { childList: true, subtree: true });

    reload();
  }

  function boot() {
    if (start.done) return;
    // The app's campaign data and the sidecart element both arrive on their own
    // schedule; give them a window to show up rather than one chance.
    if (!sidecart()) return;
    start.done = true;
    start();
  }

  function watchForBoot() {
    boot();
    if (start.done) return;
    var interval = setInterval(function () {
      boot();
      if (start.done) clearInterval(interval);
    }, 400);
    setTimeout(function () { clearInterval(interval); }, 15000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watchForBoot);
  } else {
    watchForBoot();
  }
})();
